import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Plus, RefreshCw, Search, X } from "lucide-react";
import {
  API,
  auth,
  isDue,
  LEAD_SOURCES,
  LEAD_STATUSES,
  PRODUCT_INTERESTS,
  statusMeta,
  inputCls,
  labelCls,
} from "./crm";

const EMPTY_LEAD = {
  full_name: "",
  business_name: "",
  business_email: "",
  phone_whatsapp: "",
  country: "",
  vanilla_type: "Vanilla Planifolia Grade A",
  estimated_quantity: "",
  source: "Instagram",
  notes: "",
  follow_up_date: "",
};

export const LeadsTable = ({ token, reloadKey, onOpenDetail }) => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads?limit=500`, auth(token));
      setLeads(data.leads);
      setTotal(data.total);
    } catch {
      /* shell handles 401 */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const updateStatus = async (leadId, status) => {
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { status }, auth(token));
    } catch {
      load();
    }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/leads/export`, { ...auth(token), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vgt-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/leads`,
        { ...addForm, follow_up_date: addForm.follow_up_date || null },
        auth(token)
      );
      setShowAdd(false);
      setAddForm(EMPTY_LEAD);
      load();
    } finally {
      setSaving(false);
    }
  };

  const counts = leads.reduce((acc, l) => {
    const s = l.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const q = search.trim().toLowerCase();
  const filtered = leads.filter((l) => {
    if (statusFilter !== "all" && (l.status || "new") !== statusFilter) return false;
    if (sourceFilter !== "all" && (l.source || "Website") !== sourceFilter) return false;
    if (!q) return true;
    return [l.full_name, l.business_name, l.business_email, l.country]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  const set = (k) => (e) => setAddForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p data-testid="admin-leads-total" className="text-sm text-[#6E5B4F]">
          {total} total lead{total === 1 ? "" : "s"}
        </p>
        <div className="flex gap-2.5">
          <button
            data-testid="admin-add-lead-button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-[#D4A359] hover:bg-[#c2924a] text-[#1C120C] text-sm font-bold rounded-full px-4 py-2.5 transition-colors"
          >
            <Plus size={14} /> Add Lead
          </button>
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-[#E6DEC3] hover:border-[#2E4F3E] text-[#2C1A0C] text-sm font-semibold rounded-full px-4 py-2.5 transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            data-testid="admin-export-button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] text-sm font-semibold rounded-full px-4 py-2.5 transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div data-testid="admin-status-filters" className="flex flex-wrap gap-2 flex-1">
          <button
            data-testid="admin-filter-all"
            onClick={() => setStatusFilter("all")}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3.5 py-2 transition-colors ${
              statusFilter === "all" ? "bg-[#2E4F3E] text-[#FDFBF7] border-[#2E4F3E]" : "bg-white text-[#2C1A0C] border-[#E6DEC3] hover:border-[#2E4F3E]"
            }`}
          >
            All <span className={statusFilter === "all" ? "opacity-70" : "text-[#6E5B4F]"}>{leads.length}</span>
          </button>
          {LEAD_STATUSES.map(([key, label]) =>
            (counts[key] || 0) > 0 || statusFilter === key ? (
              <button
                key={key}
                data-testid={`admin-filter-${key}`}
                onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3.5 py-2 transition-colors ${
                  statusFilter === key ? "bg-[#2E4F3E] text-[#FDFBF7] border-[#2E4F3E]" : "bg-white text-[#2C1A0C] border-[#E6DEC3] hover:border-[#2E4F3E]"
                }`}
              >
                {label} <span className={statusFilter === key ? "opacity-70" : "text-[#6E5B4F]"}>{counts[key] || 0}</span>
              </button>
            ) : null
          )}
        </div>
        <div className="flex gap-2.5">
          <select
            data-testid="admin-source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-white border border-[#E6DEC3] rounded-full px-4 py-2.5 text-sm text-[#2C1A0C] focus:outline-none focus:ring-2 focus:ring-[#2E4F3E]/40"
          >
            <option value="all">All sources</option>
            {LEAD_SOURCES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="relative sm:w-64">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6E5B4F]" />
            <input
              data-testid="admin-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, country…"
              className="w-full bg-white border border-[#E6DEC3] rounded-full pl-10 pr-4 py-2.5 text-sm text-[#2C1A0C] placeholder:text-[#6E5B4F]/50 focus:outline-none focus:ring-2 focus:ring-[#2E4F3E]/40 focus:border-[#2E4F3E] transition"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E6DEC3] rounded-3xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table data-testid="admin-leads-table" className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="bg-[#F5EFE6] text-left font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C3A21]">
                {["Date", "Name", "Company", "Country", "Product", "Source", "Follow-Up", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3.5 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-[#6E5B4F]">
                    {loading ? "Loading…" : q ? `No leads match "${search.trim()}".` : statusFilter !== "all" ? "No leads with this status." : "No leads yet. New quotation requests will appear here."}
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-t border-[#F5EFE6] align-top hover:bg-[#FDFBF7]">
                  <td className="px-4 py-3.5 whitespace-nowrap text-[#6E5B4F]">
                    <span className="inline-flex items-center gap-2">
                      {new Date(l.created_at).toLocaleDateString()}
                      {Date.now() - new Date(l.created_at).getTime() < 86400000 && (
                        <span data-testid={`admin-lead-new-badge-${l.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-[#25D366]/15 text-[#1EAE52] border border-[#25D366]/40 rounded-full px-2 py-0.5">
                          New
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <button
                      data-testid={`admin-lead-open-${l.id}`}
                      onClick={() => onOpenDetail(l.id)}
                      className="font-semibold text-[#2E4F3E] hover:text-[#1B3B2B] underline underline-offset-2"
                    >
                      {l.full_name}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{l.business_name}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{l.country || "—"}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{l.vanilla_type}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[#6E5B4F]">{l.source || "Website"}</td>
                  <td className={`px-4 py-3.5 whitespace-nowrap font-semibold ${isDue(l) ? "text-red-600" : "text-[#6E5B4F]"}`}>
                    {l.follow_up_date || "—"}
                    {isDue(l) && <span className="ml-1.5 text-[10px] uppercase tracking-wider">due</span>}
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <select
                      data-testid={`admin-lead-status-${l.id}`}
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full border px-3 py-1.5 cursor-pointer focus:outline-none ${statusMeta(l.status || "new")[2]}`}
                    >
                      {LEAD_STATUSES.map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-[60] bg-[#1C120C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form
            data-testid="add-lead-form"
            onSubmit={addLead}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif font-bold text-xl text-[#2C1A0C]">Add Lead Manually</h3>
              <button type="button" data-testid="add-lead-close" onClick={() => setShowAdd(false)} className="text-[#6E5B4F] hover:text-[#2C1A0C]">
                <X size={20} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input data-testid="add-lead-name" required minLength={2} className={inputCls} value={addForm.full_name} onChange={set("full_name")} />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <input className={inputCls} value={addForm.business_name} onChange={set("business_name")} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" className={inputCls} value={addForm.business_email} onChange={set("business_email")} />
              </div>
              <div>
                <label className={labelCls}>Phone / WhatsApp</label>
                <input className={inputCls} value={addForm.phone_whatsapp} onChange={set("phone_whatsapp")} />
              </div>
              <div>
                <label className={labelCls}>Country</label>
                <input className={inputCls} value={addForm.country} onChange={set("country")} />
              </div>
              <div>
                <label className={labelCls}>Product Interest</label>
                <select className={inputCls} value={addForm.vanilla_type} onChange={set("vanilla_type")}>
                  {PRODUCT_INTERESTS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Est. Quantity</label>
                <input className={inputCls} value={addForm.estimated_quantity} onChange={set("estimated_quantity")} placeholder="e.g. 25 kg" />
              </div>
              <div>
                <label className={labelCls}>Lead Source</label>
                <select data-testid="add-lead-source" className={inputCls} value={addForm.source} onChange={set("source")}>
                  {LEAD_SOURCES.filter((s) => s !== "Website" && s !== "Google Ads").map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Next Follow-Up Date</label>
                <input type="date" className={inputCls} value={addForm.follow_up_date} onChange={set("follow_up_date")} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes</label>
                <textarea rows={3} className={inputCls} value={addForm.notes} onChange={set("notes")} />
              </div>
            </div>
            <button
              data-testid="add-lead-submit"
              type="submit"
              disabled={saving}
              className="mt-5 w-full bg-[#2E4F3E] hover:bg-[#1B3B2B] disabled:opacity-60 text-[#FDFBF7] font-semibold rounded-full px-6 py-3.5 text-sm transition-colors"
            >
              {saving ? "Saving…" : "Add Lead"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
